import {
  BrandColorField,
  BusinessConfigData,
  BusinessConfigField,
  DisplayFontField,
  RawBusinessConfig,
} from "../../../interfaces/BusinessObject";

/**
 * Every BusinessConfigData key except brandColor/displayFont — all share the
 * same wire shape (`{ is_enabled, value? }`) and are converted the same way
 * below. brandColor/displayFont are handled separately since neither has an
 * is_enabled flag at all (see BrandColorField/DisplayFontField's doc
 * comments).
 */
const REGULAR_FIELDS = [
  "showcaseProductImages",
  "enableCollection",
  "enableGallerySections",
  "collapseAllProductSections",
  "enableOnlineOrdering",
  "enableOfflineOrdering",
  "slotCalendar",
] as const;

/**
 * `{ is_enabled, value? }` (wire) -> `{ isEnabled, value? }` (TS). Anything
 * that isn't a well-formed object with a boolean is_enabled is dropped
 * (returns undefined) rather than guessing a default — an unconfigured
 * field just isn't present in BusinessConfigData, and every consumer already
 * owns its own default (see BusinessConfigData's doc comment).
 */
function convertRawConfigField<T>(raw: unknown): BusinessConfigField<T> | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const obj = raw as Record<string, unknown>;
  const isEnabled = obj["is_enabled"];
  if (typeof isEnabled !== "boolean") return undefined;
  return "value" in obj ? { isEnabled, value: obj["value"] as T } : { isEnabled };
}

/** brand_color's / display_font's wire shape is just `{ value?: string }` —
 *  no is_enabled. Shared by both since they're structurally identical. */
function convertRawBareValueField(raw: unknown): { value: string } | undefined {
  if (!raw || typeof raw !== "object") return undefined;
  const obj = raw as Record<string, unknown>;
  if (typeof obj["value"] !== "string") return undefined;
  return { value: obj["value"] };
}

/**
 * Converts the raw GET/PUT/DELETE /config response body (`contents[0]`)
 * into BusinessConfigData. `null`/`undefined` (e.g. a business whose config
 * row doesn't exist yet) converts to `{}`, same as every field being absent.
 */
export function convertRawBusinessConfig(
  raw: RawBusinessConfig | null | undefined
): BusinessConfigData {
  const config: BusinessConfigData = {};
  if (!raw) return config;

  for (const field of REGULAR_FIELDS) {
    const converted = convertRawConfigField(raw[field]);
    if (converted !== undefined) {
      (config as Record<string, unknown>)[field] = converted;
    }
  }

  const brandColor: BrandColorField | undefined = convertRawBareValueField(raw.brandColor);
  if (brandColor !== undefined) config.brandColor = brandColor;

  const displayFont: DisplayFontField | undefined = convertRawBareValueField(raw.displayFont);
  if (displayFont !== undefined) config.displayFont = displayFont;

  return config;
}
